/* ============================================
   Doc & Glo Header — Interactions
   - Desktop mega menu (hover + click + keyboard)
   - Mobile drawer with accordion navigation
   - Search overlay
   - Announcement bar carousel
   - Sticky header scroll behavior
   ============================================ */

(function () {
  'use strict';

  /* ---- Desktop Mega Menu ---- */
  class DGMegaMenu {
    constructor(triggerEl) {
      this.trigger = triggerEl;
      this.menuId = triggerEl.dataset.dgMegaTrigger;
      this.menu = document.querySelector(`[data-dg-mega="${this.menuId}"]`);
      this.button = triggerEl.querySelector('button[aria-controls]');
      this.openTimer = null;
      this.closeTimer = null;
      this.OPEN_DELAY = 120;
      this.CLOSE_DELAY = 250;

      if (!this.menu || !this.button) return;
      this.bind();
    }

    bind() {
      // Hover
      this.trigger.addEventListener('mouseenter', () => this.scheduleOpen());
      this.trigger.addEventListener('mouseleave', () => this.scheduleClose());
      this.menu.addEventListener('mouseenter', () => this.cancelClose());
      this.menu.addEventListener('mouseleave', () => this.scheduleClose());

      // Click toggle
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });

      // Keyboard
      this.menu.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    isOpen() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    scheduleOpen() {
      this.cancelClose();
      this.openTimer = setTimeout(() => this.open(), this.OPEN_DELAY);
    }

    scheduleClose() {
      this.cancelOpen();
      this.closeTimer = setTimeout(() => this.close(), this.CLOSE_DELAY);
    }

    cancelOpen() {
      if (this.openTimer) { clearTimeout(this.openTimer); this.openTimer = null; }
    }

    cancelClose() {
      if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    }

    toggle() {
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      this.cancelClose();
      // Close any other open mega menus
      DGMegaMenu.instances.forEach((inst) => {
        if (inst !== this && inst.isOpen()) inst.close();
      });
      this.button.setAttribute('aria-expanded', 'true');
      this.menu.classList.add('is-active');
      this.trigger.classList.add('is-active');
    }

    close() {
      this.cancelOpen();
      this.button.setAttribute('aria-expanded', 'false');
      this.menu.classList.remove('is-active');
      this.trigger.classList.remove('is-active');
    }

    onKeydown(e) {
      const links = [...this.menu.querySelectorAll('a, button')];
      const idx = links.indexOf(document.activeElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (idx < links.length - 1) links[idx + 1].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (idx > 0) links[idx - 1].focus();
          else this.button.focus();
          break;
        case 'Tab':
          if (!e.shiftKey && idx === links.length - 1) this.close();
          break;
      }
    }
  }
  DGMegaMenu.instances = [];

  /* ---- Mobile Drawer ---- */
  class DGMobileMenu {
    constructor() {
      this.drawer = document.querySelector('[data-dg-mobile]');
      this.overlay = document.querySelector('[data-dg-mobile-overlay]');
      this.toggleBtn = document.querySelector('[data-dg-mobile-toggle]');
      this.closeBtn = document.querySelector('[data-dg-mobile-close]');

      if (!this.drawer) return;
      this.bind();
    }

    bind() {
      if (this.toggleBtn) this.toggleBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      // Accordion toggles
      this.drawer.querySelectorAll('[data-dg-mobile-accordion]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleAccordion(btn));
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return this.drawer.classList.contains('is-active');
    }

    open() {
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-active');
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-active');
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggleAccordion(btn) {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const submenu = btn.nextElementSibling;
      if (!submenu) return;

      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      submenu.classList.toggle('is-active', !expanded);
    }
  }

  /* ---- Search Overlay ---- */
  class DGSearch {
    constructor() {
      this.overlay = document.querySelector('[data-dg-search-overlay]');
      this.toggleBtns = document.querySelectorAll('[data-dg-search-toggle]');
      this.closeBtns = document.querySelectorAll('[data-dg-search-close]');
      this.input = document.querySelector('[data-dg-search-input]');

      if (!this.overlay) return;
      this.bind();
    }

    bind() {
      this.toggleBtns.forEach((btn) => btn.addEventListener('click', () => this.open()));
      this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return this.overlay.classList.contains('is-active');
    }

    open() {
      this.overlay.classList.add('is-active');
      this.overlay.setAttribute('aria-hidden', 'false');
      if (this.input) setTimeout(() => this.input.focus(), 100);
    }

    close() {
      this.overlay.classList.remove('is-active');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
  }

  /* ---- Announcement Carousel ---- */
  class DGAnnouncement {
    constructor() {
      this.carousel = document.querySelector('[data-dg-announcement-carousel]');
      if (!this.carousel) return;

      this.slides = this.carousel.querySelectorAll('.dg-announcement__slide');
      if (this.slides.length <= 1) return;

      this.currentIndex = 0;
      this.interval = 4000;
      this.start();
    }

    start() {
      setInterval(() => this.next(), this.interval);
    }

    next() {
      this.slides[this.currentIndex].classList.remove('is-active');
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.slides[this.currentIndex].classList.add('is-active');
    }
  }

  /* ---- Sticky Header ---- */
  class DGStickyHeader {
    constructor() {
      this.header = document.querySelector('[data-dg-header]');
      if (!this.header || !this.header.classList.contains('dg-header--sticky')) return;

      this.lastScrollY = 0;
      this.scrollThreshold = 80;
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    }

    onScroll() {
      const scrollY = window.scrollY;
      if (scrollY > this.scrollThreshold) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }
      this.lastScrollY = scrollY;
    }
  }

  /* ---- Cart ---- */
  /* Cart uses <a href="/cart"> — Kaching Cart app intercepts clicks automatically.
     No custom JS needed. Falls back to /cart page if Kaching is disabled. */

  /* ---- Global close on click outside ---- */
  function setupGlobalClose() {
    document.addEventListener('click', (e) => {
      DGMegaMenu.instances.forEach((inst) => {
        if (inst.isOpen() && !inst.trigger.contains(e.target)) {
          inst.close();
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        DGMegaMenu.instances.forEach((inst) => {
          if (inst.isOpen()) {
            inst.close();
            inst.button.focus();
          }
        });
      }
    });
  }

  /* ---- Thumbnail Lazy Loader ---- */
  /* Shopify's all_products Liquid object is limited to ~20 unique handles per page.
     This fetches product images via the AJAX API for any thumbnails that didn't resolve. */
  class DGThumbnailLoader {
    constructor() {
      // Find ALL product thumbnails (both desktop and mobile)
      var allThumbs = document.querySelectorAll('img[data-product-handle]');
      if (!allThumbs.length) return;

      // Collect unique handles
      var handles = new Set();
      allThumbs.forEach(function(img) {
        var handle = img.getAttribute('data-product-handle');
        if (handle) handles.add(handle);
      });

      // Fetch each product and update ALL matching thumbnails + savings badges
      handles.forEach(function(handle) {
        fetch('/products/' + handle + '.js')
          .then(function(r) { return r.json(); })
          .then(function(product) {
            // Update thumbnails
            if (product.featured_image) {
              var src = product.featured_image;
              var sep = src.indexOf('?') !== -1 ? '&' : '?';
              var imgUrl = src + sep + 'width=80';

              document.querySelectorAll('img[data-product-handle="' + handle + '"]').forEach(function(img) {
                img.src = imgUrl;
                img.alt = product.title;
                img.classList.remove('dg-mega__thumb--pending');
              });
            }

            // Populate savings badge if compare_at_price > price
            // Only applies to items with [data-savings-for="{handle}"] companion element
            var savingsEls = document.querySelectorAll('[data-savings-for="' + handle + '"]');
            if (savingsEls.length && product.compare_at_price && product.compare_at_price > product.price) {
              var savingsCents = product.compare_at_price - product.price;
              var savingsDollars = Math.round(savingsCents / 100);
              savingsEls.forEach(function(el) {
                el.textContent = 'Save $' + savingsDollars;
              });
            }
          })
          .catch(function() { /* Product not found — leave placeholder */ });
      });
    }
  }

  /* ---- Init ---- */
  function init() {
    // Mega menus
    document.querySelectorAll('[data-dg-mega-trigger]').forEach((el) => {
      DGMegaMenu.instances.push(new DGMegaMenu(el));
    });
    setupGlobalClose();

    // Mobile menu
    new DGMobileMenu();

    // Search
    new DGSearch();

    // Announcement carousel
    new DGAnnouncement();

    // Sticky header
    new DGStickyHeader();

    // Cart: handled by Kaching Cart app embed (intercepts <a href="/cart">)

    // Lazy-load thumbnails that exceeded Liquid's all_products limit
    new DGThumbnailLoader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
